import type { NodeV2 } from '@vedalib/editor'
import cn from 'classnames'
import React, { useMemo } from 'react'

import { useProjectContext } from 'services/Store/Project/hooks'
import { getNodeStyle, getPlaceholders, mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'

import * as s from './DragPlaceholderFlex.module.scss'

interface IPlaceholderProps {
  block: Block
  node: NodeV2
  mode: IBlockMode
  nodeRef: React.RefObject<HTMLDivElement>
}

const DragPlaceholderFlex: React.FC<IPlaceholderProps> = ({ mode, block, node }) => {
  const dnd = useProjectContext(getPlaceholders, node.id)
  const styles = useProjectContext(getNodeStyle, block.uuid, node.id)
  const direction = (styles?.display === 'flex' && styles?.flexDirection) || 'row'
  const isEveryAbsolute = useMemo(
    () =>
      dnd?.targetIds?.every((id) => {
        const node = block.schema.nodes[id] as NodeV2
        const style = mergeNodeStyle(node?.style || {}, mode.deviceMode)
        return style?.flexFlow === 'absolute'
      }),
    [block.schema.nodes, dnd?.targetIds, mode.deviceMode],
  )

  const coords = useMemo(() => {
    return Object.values(dnd?.coords || {}).map((coord) => {
      const [topMargin, rightMargin, bottomMargin, leftMargin] = coord.offset
      return {
        top: coord.top != null ? coord.top : undefined,
        left: coord.left != null ? coord.left : undefined,
        bottom: coord.bottom != null ? coord.bottom : undefined,
        right: coord.right != null ? coord.right : undefined,
        width: coord.width + (leftMargin || 0) + (rightMargin || 0),
        height: coord.height + (topMargin || 0) + (bottomMargin || 0),
      }
    })
  }, [dnd?.coords])

  return (
    <>
      {!isEveryAbsolute && (
        <>
          <div className={s.hatch} />
          <div
            className={cn(s.root, s[direction])}
            style={{ top: dnd?.flex?.y || 0, left: dnd?.flex?.x || 0 }}
          >
            <div className={cn(s.indicator, s[direction])} />
          </div>
        </>
      )}
      {coords?.map((style, idx) => (
        <div className={s.element} key={dnd?.targetIds?.[idx]} style={style} />
      ))}
    </>
  )
}

export default DragPlaceholderFlex
